import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const SIDE_IMG = "https://images.pexels.com/photos/34155535/pexels-photo-34155535.jpeg";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await login(email, password);
      navigate(user.role === "admin" ? "/admin" : (from || "/"), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen grid md:grid-cols-2" data-testid="login-page">
      <div className="relative hidden md:block bg-primary overflow-hidden">
        <img src={SIDE_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-primary/50" />
        <div className="relative h-full flex flex-col justify-between p-12 text-primary-foreground">
          <span className="font-display text-2xl">Coastal Chemistry</span>
          <h2 className="font-display text-5xl font-light leading-[0.95] tracking-tight max-w-sm">Welcome back to the shoreline.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-24 md:py-0">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-sm">
          <p className="label text-accent mb-4">/ Sign in</p>
          <h1 className="font-display text-4xl font-light tracking-tight mb-10">Account access</h1>

          {error && <p className="mb-6 text-sm text-destructive border border-destructive px-4 py-3" data-testid="login-error">{error}</p>}

          <form onSubmit={submit} className="space-y-6" data-testid="login-form">
            <div>
              <label className="label text-muted-foreground block mb-2">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-accent transition-colors" data-testid="login-email" />
            </div>
            <div>
              <label className="label text-muted-foreground block mb-2">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-accent transition-colors" data-testid="login-password" />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-accent transition-colors" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
            </div>
            <button type="submit" disabled={busy} className="w-full h-14 bg-primary text-primary-foreground label hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="login-submit">
              {busy ? "Signing in…" : "Sign in"} <ArrowRight size={16} strokeWidth={1.5} />
            </button>
          </form>

          <p className="mt-8 text-sm text-muted-foreground">
            No account? <Link to="/register" className="text-accent border-b border-accent" data-testid="to-register">Create one</Link>
          </p>
          <p className="mt-10 label text-muted-foreground/70 tracking-[0.2em]">Research Use Only · Not For Human Consumption</p>
        </motion.div>
      </div>
    </main>
  );
}
